/**
 * Offline tills (`orders.offline`): receipt numbers in advance, and sales sent
 * when the connection comes back.
 *
 * While online, a till asks for a block of receipt numbers from its site's own
 * series and keeps it on the device. With no connection it numbers its
 * receipts from those blocks, in order, and keeps each sale. When the line
 * returns it sends them one at a time; the server records each once, keyed by
 * the sale's own `client_id`, so sending one twice is safe.
 */

import { useMutation } from '@tanstack/react-query';

import type { ApiClient, ApiError } from './client';
import type { Receipt } from './fiscal';
import { useApi } from './provider';

export interface OfflineBlock {
  id: string;
  terminal_id: string;
  series: string;
  first_number: number;
  last_number: number;
}

export interface OfflineSaleLine {
  item_id: string;
  variant_id?: string | null;
  modifier_option_ids?: string[];
  qty: string;
  /** What the guest was charged for one, options included. */
  unit_price: string;
  note?: string | null;
}

export interface OfflineSalePayment {
  tender_type: string;
  amount: string;
  tip?: string;
  /** What changed hands, when a note covered more than the bill. */
  tendered?: string | null;
  at: string;
  card_reader_id?: string | null;
  auth_code?: string | null;
  card_last4?: string | null;
}

export interface OfflineSale {
  client_id: string;
  location_id: string;
  terminal_id: string;
  opened_at: string;
  closed_at: string;
  receipt_number: number;
  table_id?: string | null;
  guest_count?: number;
  lines: OfflineSaleLine[];
  payments: OfflineSalePayment[];
}

export interface RecordedOfflineSale {
  client_id: string;
  order_id: string;
  receipt: Receipt;
  /** False when the server already had this sale. */
  new: boolean;
}

export function useReserveOfflineBlock() {
  const api = useApi();
  return useMutation<OfflineBlock, ApiError, { location_id: string; terminal_id: string }>({
    mutationFn: (body) => api.post<OfflineBlock>('/v1/fiscal/offline-blocks', body),
  });
}

/** Send one offline sale. A plain function: the queue calls it outside React. */
export function sendOfflineSale(
  api: ApiClient,
  sale: OfflineSale,
): Promise<RecordedOfflineSale> {
  return api.post<RecordedOfflineSale>('/v1/fiscal/offline-sales', sale);
}
